import { useState } from "react";
import PropTypes from "prop-types";
import makeStyles from "@mui/styles/makeStyles";
import Moment from "moment";

import { InfoWindow } from "@react-google-maps/api";
import { Icon, Button, Dialog, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CarCrashIcon from "@mui/icons-material/CarCrash";

import ClockIcon from "@mui/icons-material/AccessTime";
import SpeedIcon from "@mui/icons-material/Speed";
import DriverIcon from "@mui/icons-material/PermIdentity";
import JourneyTimeIcon from "@mui/icons-material/HourglassEmptyOutlined";
import useUnits from "../hooks/useUnits";
import Measurement from "./Measurement";

const useStyles = makeStyles((theme) => ({
  createIncidentButton: {
    padding: "3px 10px 3px 3px",
    textTransform: "none",
    fontWeight: "normal",
    width: "100%",
    marginTop: theme.spacing(2),
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  container: {
    display: "inline-flex",
    alignItems: "center",
  },
  icons: {
    paddingRight: 5,
  },
  snapshotBox: {
    width: "100%",
    height: "auto",
    aspectRatio: "16/9",
    overflow: "hidden",
    background: theme.palette.action.hover,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  snapshotImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    cursor: "pointer",
    display: "block",
  },
  dialogContent: {
    position: "relative",
    padding: 0,
    overflow: "hidden",
    background: "rgba(209, 196, 196, 0.85)",
    width: "min(82vw, calc(72vh * 16/9))",
    height: "min(72vh, calc(82vw * 9/16))",
    aspectRatio: "16/9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogImg: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "contain",
  },
  closeBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 100,
    color: "#fff",
    background: "rgba(0,0,0,0.45)",
    backdropFilter: "blur(8px)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
    transition: "transform 0.15s ease, background 0.2s ease",
    "&:hover": {
      background: "rgba(0,0,0,0.7)",
      transform: "scale(1.08)",
    },
  },
}));

const VehicleTrackingNodeInfoWindow = ({
  node,
  journey,
  onCloseTrackingInfoWindow,
  onCreateIncident,
  incidentsEnabled,
  supportsVideo,
  imageUrl,
  imageLoading,
}) => {
  const classes = useStyles();
  const { formatSpeed } = useUnits();
  const [openPreview, setOpenPreview] = useState(false);

  const handleOpenPreview = () => {
    if (!imageUrl) return;
    setOpenPreview(true);
  };
  const handleClosePreview = () => setOpenPreview(false);

  return (
    <>
      <InfoWindow
        position={node.coord}
        onCloseClick={onCloseTrackingInfoWindow}
      >
        <div style={{ maxWidth: 220 }}>
          {Boolean(node.heading) && (
            <>
              <Icon style={{ transform: `rotate(${node.heading}deg)` }}>
                navigation
              </Icon>
              <br />
            </>
          )}

          <div className={classes.container}>
            <ClockIcon className={classes.icons} fontSize="small" />
            <b>Time</b>: {Moment(node.timestamp).format("LTS")}
          </div>
          <br />

          <div className={classes.container}>
            <SpeedIcon className={classes.icons} fontSize="small" />
            <b>Speed</b>: <Measurement>{formatSpeed(node.speed)}</Measurement>
          </div>
          <br />

          {journey && (
            <>
              <br />
              <div className={classes.container}>
                <JourneyTimeIcon className={classes.icons} fontSize="small" />
                <b>Driving time</b>: {Moment(journey.start).format("LT")} -{" "}
                {Moment(journey.end).format("LT")}
              </div>
              <br />
              <div className={classes.container}>
                <DriverIcon className={classes.icons} fontSize="small" />
                <b>Driver</b>: {journey.driver?.name ?? "-"}
              </div>
              <br />
              <br />
            </>
          )}

          {imageLoading && (
            <div style={{ marginBottom: 8, fontSize: 12 }}>
              Loading snapshot…
            </div>
          )}

          {!imageLoading && imageUrl && (
            <div className={classes.snapshotBox}>
              <img
                src={imageUrl}
                alt="Snapshot"
                className={classes.snapshotImg}
                onClick={handleOpenPreview}
              />
            </div>
          )}

          {supportsVideo && incidentsEnabled && (
            <Button
              size="small"
              variant="outlined"
              className={classes.createIncidentButton}
              onClick={onCreateIncident(node.timestamp)}
            >
              <CarCrashIcon className={classes.leftIcon} /> Create incident
            </Button>
          )}
        </div>
      </InfoWindow>

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        maxWidth={false}
        PaperProps={{
          style: {
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <DialogContent className={classes.dialogContent}>
          <IconButton
            aria-label="close"
            className={classes.closeBtn}
            onClick={handleClosePreview}
            size="large"
          >
            <CloseIcon />
          </IconButton>

          {imageUrl && (
            <img
              src={imageUrl}
              alt="Snapshot full"
              className={classes.dialogImg}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

VehicleTrackingNodeInfoWindow.propTypes = {
  node: PropTypes.object.isRequired,
  onCloseTrackingInfoWindow: PropTypes.func.isRequired,
  onCreateIncident: PropTypes.func.isRequired,
  supportsVideo: PropTypes.bool.isRequired,
  incidentsEnabled: PropTypes.bool.isRequired,
};

export default VehicleTrackingNodeInfoWindow;
